import { RootState } from '@/typings';
import { ElMessage, ElSkeleton, isEmpty } from '@qsxy/element-plus-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import ChatContainer, { Message } from './components/ChatContainer';
import { doChat, getSuggsted, renameConversation, renameLocalConversation } from './api';
import dayjs from 'dayjs';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '@/AppContext';
import { useFetchMessages } from '@/hooks/useFetchMessages';
import { useForceUpdate } from '@/hooks/useForceUpdate';
import last from 'lodash/last';
import { useRequest } from 'ahooks';
import { useCheckCount } from '@/hooks/useCheckCount';

const ChatView = () => {
	const loginUser = useSelector<RootState, RootState['user']>(state => state.user ?? {});

	const location = useLocation();
	const params = useParams();
	const navigate = useNavigate();
	const { conversationTitle, localConversationId, conversationType, refreshConversation } = useAppContext();
	const { getMessages, handleMessage, loading } = useFetchMessages();
	const { forceUpdate } = useForceUpdate();
	const { checkChatCount } = useCheckCount();

	const [messages, setMessages] = useState<Message[]>([]);
	const [isAnswering, setIsAnswering] = useState(false);

	const thinkingTime = useRef(null);
	const startTime = useRef(null);
	const conversationId = useRef(null);
	// 新会话是否已重命名
	const renameFlag = useRef(false);
	const chatContainerRef = useRef(null);

	// 是否新会话
	const isNewConversation = useMemo(() => params.id.length === 11, [params.id.length]);

	const {
		data: suggestions,
		run,
		mutate,
	} = useRequest(messageId => getSuggsted({ user: loginUser.username, message_id: messageId }), {
		manual: true,
		onSuccess() {
			forceUpdate();
		},
	});

	const handleStreamSend = useCallback(
		async (message: string, callback?: (conversationId: string) => void) => {
			if (isEmpty(message)) {
				return;
			}
			const checkResult = await checkChatCount();
			if (!checkResult) {
				return;
			}
			const msg: Message = {
				role: 'user',
				content: message,
			};
			setMessages(prev => [...prev, msg]);
			mutate([]);

			setIsAnswering(true);
			startTime.current = new Date();
			thinkingTime.current = null;

			try {
				// 流式回调处理
				const streamCallback = async (chunk: string, conversation: Message['conversation']) => {
					// 思考未结束时间递增
					if (chunk.indexOf('</think>') === -1 && startTime.current != null) {
						thinkingTime.current = (dayjs(new Date()).diff(startTime.current, 'milliseconds') / 1000).toFixed(1);
					}
					// 新会话记录会话id，并执行一次重命名会话
					if (isNewConversation && conversation.id) {
						conversationId.current = conversation.id;
						if (!renameFlag.current) {
							renameFlag.current = true;
							callback?.(conversation.id);
						}
					}
					setMessages(prev => {
						const answeringIdx = prev.findIndex(item => item.loading);
						const curMsg =
							answeringIdx === -1
								? {
										role: 'assistant' as const,
										content: chunk,
										thinkingTime: thinkingTime.current,
										loading: true,
										conversation,
								  }
								: { ...prev[answeringIdx], content: chunk, thinkingTime: thinkingTime.current };
						if (answeringIdx !== -1) {
							prev.splice(answeringIdx, 1, curMsg);
							return [...prev];
						}
						return [...prev, curMsg];
					});
				};

				const { result, conversation } = await doChat(
					{
						inputs: isNewConversation ? { title: message } : {},
						query: message,
						response_mode: 'streaming',
						conversation_id: conversationId.current,
						user: loginUser.username,
						auto_generate_name: false,
						conversation_type: conversationType.current,
					},
					streamCallback,
				);

				setIsAnswering(false);
				startTime.current = null;
				// 新会话完成后跳转至新路由，临时id转会话id，防止临时id时页面刷新报错
				if (conversationId.current && isNewConversation) {
					navigate(`/chat/${conversationId.current}`);
					localStorage.setItem('ttn-conversation-prev-id', params.id);
				}
				// refreshConversation?.current?.();

				const assistantMessage: Message = {
					role: 'assistant' as const,
					content: handleMessage(result),
					thinkingTime: thinkingTime.current,
					conversation,
					loading: false,
				};
				setMessages(prev => {
					prev.splice(prev.length - 1, 1, assistantMessage);
					return prev;
				});
				run(conversation.messageId);
			} catch (error) {
				ElMessage.error('发送消息失败，请重试');
				setIsAnswering(false);
			}
		},
		[checkChatCount, conversationType, handleMessage, isNewConversation, loginUser.username, mutate, navigate, params.id, run],
	);

	useEffect(() => {
		// 新会话完成后跳转新路由地址不执行任务操作
		const prevId = localStorage.getItem('ttn-conversation-prev-id');
		if (!prevId) {
			setMessages([]);
			mutate([]);
			if (isNewConversation) {
				conversationId.current = null;
				renameFlag.current = false;
				handleStreamSend(conversationTitle.current, id => {
					if (loginUser?.isVisitor) {
						renameConversation({ user: loginUser.username, name: conversationTitle.current, conversation_id: id });
					}
				});
			} else {
				if (loginUser?.username) {
					getMessages(params.id, msg => {
						setMessages(msg);
						setTimeout(() => chatContainerRef.current.scrollToBottom(), 100);
						run(last(msg).conversation.messageId);
					});
					conversationId.current = params?.id;
				}
			}
		} else {
			localStorage.setItem('ttn-conversation-prev-id', '');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location, loginUser?.username]);

	return (
		<ElSkeleton
			visible={loading}
			animated
			formatter={
				<div className="ttn-chat-container ttn-chat-loading">
					<div className="chat-messages">
						<div className="ttn-message ttn-message-user">
							<ElSkeleton style={{ marginTop: 30 }} variant="circle" animated />
						</div>

						<div className="ttn-message ttn-message-ai">
							<ElSkeleton rows={4} variant="circle" animated />
							<ElSkeleton rows={2} variant="circle" animated />
							<ElSkeleton rows={4} variant="circle" animated />
						</div>
					</div>
				</div>
			}
		>
			<ChatContainer
				ref={chatContainerRef}
				title={conversationTitle.current}
				messages={messages}
				suggestions={suggestions}
				loading={isAnswering}
				send={handleStreamSend}
				onRename={value =>
					renameLocalConversation({ id: localConversationId.current, title: value }).then(() => {
						conversationTitle.current = value;
						forceUpdate();
						refreshConversation?.current?.();
						ElMessage.success('修改成功');
					})
				}
			/>
		</ElSkeleton>
	);
};

export default ChatView;
